import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from './keys';
import { invalidate, type QueryOptions } from './query-client';
import { request } from './client';
import { tenantsApi } from './tenants';
import type {
  ListQuery,
  Paginated,
  Role,
  StaffRole,
  TenantRole,
  User,
  UserStatus,
  UserType,
} from './types';

/**
 * /users: the account directory. Staff read it (admins also write); a tenant
 * account reads the accounts on its own tenant.
 */
export const usersApi = {
  /** Staff: `tenantId` narrows to one tenant's accounts. A tenant account always gets its own. */
  list: (
    query: ListQuery & {
      userType?: UserType;
      status?: UserStatus;
      role?: string;
      deactivated?: 'true' | 'false';
      tenantId?: string;
    } = {},
  ) => request<Paginated<User>>('/users', { query }),
  get: (id: string) => request<User>(`/users/${id}`),
  /**
   * Staff account with a role (admins only); a temporary password is emailed.
   * Email is enough: names left out are derived from the address until the person corrects them.
   */
  invite: (body: { email: string; role: StaffRole; firstName?: string; lastName?: string }) =>
    request<User>('/users/invite', { method: 'POST', body }),
  /** Admins: email, status, role (must fit the account's type) or phone; `version` guards against lost updates. */
  update: (
    id: string,
    body: { email?: string; role?: Role; status?: UserStatus; phone?: string; version?: number },
  ) => request<User>(`/users/${id}`, { method: 'PATCH', body }),
  /** Switch an account off: it keeps its sign-in but sees basic details only. */
  deactivate: (id: string) => request<User>(`/users/${id}/deactivate`, { method: 'POST' }),
  reactivate: (id: string) => request<User>(`/users/${id}/reactivate`, { method: 'POST' }),
  /** Admins: soft delete. The last active admin cannot be removed. */
  remove: (id: string) => request<void>(`/users/${id}`, { method: 'DELETE' }),
};

type UsersQuery = NonNullable<Parameters<typeof usersApi.list>[0]>;
type InviteStaffBody = Parameters<typeof usersApi.invite>[0];
type UpdateUserBody = Parameters<typeof usersApi.update>[1];

/**
 * What the admin "create user" form sends. A staff account is invited through
 * /users/invite; a tenant account joins a tenant through /tenants/:id/members
 * (that is where the 3-account cap and the membership live).
 */
export type NewUser =
  | { userType: 'staff'; email: string; firstName?: string; lastName?: string; role: StaffRole }
  | {
      userType: 'tenant';
      email: string;
      firstName: string;
      lastName: string;
      role: Exclude<TenantRole, 'prospect'>;
      tenantId: string;
    };

export function useUsers(query: UsersQuery = {}, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.users.list(query),
    queryFn: () => usersApi.list(query),
    placeholderData: keepPreviousData,
    ...options,
  });
}

/** Members lists live under the tenants key, so an account change refreshes both. */
function useUserMutation<TVars, TData = User>(mutationFn: (vars: TVars) => Promise<TData>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => invalidate(queryClient, keys.users.all, keys.tenants.all),
  });
}

export function useInviteStaff() {
  return useUserMutation((body: InviteStaffBody) => usersApi.invite(body));
}

/** The directory's create form: one mutation, the endpoint follows the account type. */
export function useCreateUser() {
  return useUserMutation(async (body: NewUser): Promise<User> => {
    if (body.userType === 'staff') {
      const { email, firstName, lastName, role } = body;
      return usersApi.invite({ email, firstName, lastName, role });
    }
    const { tenantId, email, firstName, lastName, role } = body;
    const member = await tenantsApi.invite(tenantId, { email, firstName, lastName, role });
    return member.user;
  });
}

export function useUpdateUser() {
  return useUserMutation(({ id, body }: { id: string; body: UpdateUserBody }) =>
    usersApi.update(id, body),
  );
}

export function useDeactivateUser() {
  return useUserMutation((id: string) => usersApi.deactivate(id));
}

export function useReactivateUser() {
  return useUserMutation((id: string) => usersApi.reactivate(id));
}

export function useDeleteUser() {
  return useUserMutation((id: string) => usersApi.remove(id));
}
